import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';

import {
  ContainerDetailedResponse,
  ContainerPageResponse,
} from '@app/api/models';
import { ContainerPageRequest } from '@containers/interfaces/container-page-request.interface';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { ContentStatus } from '@shared/interfaces/content-status';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export interface State {
  // get list
  containersFilter: ContainerPageRequest;
  containers: EntityState<ContainerPageResponse>;
  isLoading: boolean;
  containersLoadError: boolean;
  totalContainers: number;
  // get one container to edit
  editedContainer: ContainerDetailedResponse | undefined;
  isEditedContainerLoading: boolean;
  editedContainerLoadError: boolean;
  // save container
  containerSaving: boolean;
  containerSaveError: boolean;
}

export const containerAdapter = createSliceEntityAdapter<ContainerPageResponse>(
  {
    selectId: (container) => container.id ?? '',
  }
);

export const initialState: State = {
  containersFilter: {
    status: ContentStatus.Draft,
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  containers: containerAdapter.getInitialState(),
  totalContainers: 0,
  isLoading: false,
  containersLoadError: false,
  editedContainer: undefined,
  isEditedContainerLoading: false,
  editedContainerLoadError: false,
  containerSaving: false,
  containerSaveError: false,
};

export const {
  actions: ContainersActions,
  selectors,
  ...ContainersFeature
} = createSlice({
  name: 'containers',
  initialState,
  reducers: {
    loadContainers: {
      trigger: (state, filter: PayloadAction<ContainerPageRequest>) => {
        containerAdapter.setAll(state.containers, []);
        state.containersFilter = filter;
        state.isLoading = true;
      },
      success: (
        state,
        action: PayloadAction<PageResponse<ContainerPageResponse>>
      ) => {
        containerAdapter.setAll(state.containers, action.content);
        state.totalContainers = action.totalElements ?? 0;
        state.isLoading = false;
        state.containersLoadError = false;
      },
      failure: (state) => ({
        ...state,
        containersLoadError: true,
        isLoading: false,
      }),
    },
    loadEditedContainer: {
      trigger: (state, action: PayloadAction<{ id: string }>): State => ({
        ...state,
        ...action,
        isEditedContainerLoading: true,
      }),
      success: (state, action: PayloadAction<ContainerDetailedResponse>) => {
        state.editedContainer = action;
        state.isEditedContainerLoading = false;
        state.editedContainerLoadError = false;
      },
      failure: (state) => ({
        ...state,
        containersLoadError: true,
        editedContainerLoadError: false,
      }),
    },
    createContainer: {
      trigger: (
        state,
        _action: PayloadAction<ContainerDetailedResponse>
      ): State => ({
        ...state,
        containerSaving: true,
      }),
      success: (
        state,
        _action: PayloadAction<ContainerDetailedResponse>
      ): State => ({
        ...state,
        containerSaveError: false,
        containerSaving: false,
      }),
      failure: (state): State => ({
        ...state,
        containerSaveError: true,
        containerSaving: false,
      }),
    },
    updateContainer: {
      trigger: (
        state,
        _action: PayloadAction<{
          container: ContainerDetailedResponse;
          isAdminMode?: boolean;
        }>
      ): State => ({
        ...state,
        containerSaving: true,
      }),
      success: (
        state,
        _action: PayloadAction<ContainerDetailedResponse>
      ): State => ({
        ...state,
        containerSaveError: false,
        containerSaving: false,
      }),
      failure: (state): State => ({
        ...state,
        containerSaveError: true,
        containerSaving: false,
      }),
    },
    reset: (_) => initialState,
  },
});

export const ContainersSelectors = {
  ...selectors,
  selectContainerList: createSelector(
    selectors.selectContainers,
    (containers) => Object.values(containers.entities)
  ),
  selectContainerItem: (id: string) =>
    createSelector(selectors.selectContainers, (containers) =>
      id ? containers.entities[id] : undefined
    ),
};
